import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import {
  TrendingUp,
  TrendingDown,
  Star,
  StarOff,
  AlertTriangle,
  Clock,
  Target,
  Shield,
  Activity,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const StockCard = ({ stock, rank }) => {
  const { isAuthenticated, addToWatchlist, removeFromWatchlist, isInWatchlist } = useAuth();
  const { subscribeToStock, unsubscribeFromStock } = useWebSocket();
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentPrice, setCurrentPrice] = useState(stock.currentPrice);
  const [priceChange, setPriceChange] = useState(null);

  const isWatched = isInWatchlist(stock.ticker);

  useEffect(() => {
    // Subscribe to real-time price updates
    subscribeToStock(stock.ticker);

    const handlePriceUpdate = (event) => {
      const { ticker, price, change } = event.detail;
      if (ticker === stock.ticker) {
        setPriceChange(price > currentPrice ? 'up' : price < currentPrice ? 'down' : null);
        setCurrentPrice(price);
        
        // Clear price change animation after 500ms
        setTimeout(() => setPriceChange(null), 500);
      }
    };

    window.addEventListener('priceUpdate', handlePriceUpdate);

    return () => {
      unsubscribeFromStock(stock.ticker);
      window.removeEventListener('priceUpdate', handlePriceUpdate);
    };
  }, [stock.ticker, currentPrice, subscribeToStock, unsubscribeFromStock]);

  const handleWatchlistToggle = async () => {
    if (!isAuthenticated) return;
    
    if (isWatched) {
      await removeFromWatchlist(stock.ticker);
    } else {
      await addToWatchlist(stock.ticker);
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'BUY': return 'text-success-600 bg-success-50';
      case 'SELL': return 'text-danger-600 bg-danger-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getRiskColor = (score) => {
    if (score <= 3) return 'text-success-600 bg-success-50';
    if (score <= 6) return 'text-warning-600 bg-warning-50';
    return 'text-danger-600 bg-danger-50';
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(price);
  };

  const formatPercent = (percent) => {
    return `${percent >= 0 ? '+' : ''}${percent.toFixed(2)}%`;
  };

  return (
    <div className="card hover:shadow-md transition-shadow duration-200">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="flex items-center justify-center w-8 h-8 bg-primary-100 text-primary-600 rounded-full font-bold text-sm">
            #{rank}
          </div>
          <div>
            <Link 
              to={`/stock/${stock.ticker}`}
              className="text-xl font-bold text-gray-900 hover:text-primary-600 transition-colors"
            >
              {stock.ticker}
            </Link>
            <p className="text-sm text-gray-600">{stock.companyName}</p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {isAuthenticated && (
            <button
              onClick={handleWatchlistToggle}
              className="p-2 rounded-lg hover:bg-gray-50 transition-colors"
              title={isWatched ? 'Remove from watchlist' : 'Add to watchlist'}
            >
              {isWatched ? (
                <Star className="h-5 w-5 text-warning-500 fill-current" />
              ) : (
                <StarOff className="h-5 w-5 text-gray-400" />
              )}
            </button>
          )}
          
          <span className={`badge ${getActionColor(stock.recommendation.action)}`}>
            {stock.recommendation.action}
          </span>
        </div>
      </div>

      {/* Price Info */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <div>
          <p className="text-sm text-gray-600">Current Price</p>
          <p className={`text-lg font-bold ${priceChange === 'up' ? 'price-up' : priceChange === 'down' ? 'price-down' : 'text-gray-900'}`}>
            {formatPrice(currentPrice)}
          </p>
        </div>
        
        <div>
          <p className="text-sm text-gray-600">Day Change</p>
          <p className={`text-lg font-bold flex items-center ${
            stock.dayChangePercent >= 0 ? 'text-success-600' : 'text-danger-600'
          }`}>
            {stock.dayChangePercent >= 0 ? (
              <TrendingUp className="h-4 w-4 mr-1" />
            ) : (
              <TrendingDown className="h-4 w-4 mr-1" />
            )}
            {formatPercent(stock.dayChangePercent)}
          </p>
        </div>
        
        <div>
          <p className="text-sm text-gray-600">Confidence</p>
          <p className="text-lg font-bold text-gray-900">
            {stock.recommendation.confidence}%
          </p>
        </div>
        
        <div>
          <p className="text-sm text-gray-600">Risk Score</p>
          <p className={`text-lg font-bold ${getRiskColor(stock.riskAssessment.overallScore).split(' ')[0]}`}>
            {stock.riskAssessment.overallScore}/10
          </p>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className="flex items-center space-x-2">
          <Target className="h-4 w-4 text-primary-600" />
          <div>
            <p className="text-xs text-gray-600">Entry</p>
            <p className="font-medium">{formatPrice(stock.recommendation.entryPrice)}</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <TrendingUp className="h-4 w-4 text-success-600" />
          <div>
            <p className="text-xs text-gray-600">Target</p>
            <p className="font-medium text-success-600">{formatPrice(stock.recommendation.targetPrice)}</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Shield className="h-4 w-4 text-danger-600" />
          <div>
            <p className="text-xs text-gray-600">Stop Loss</p>
            <p className="font-medium text-danger-600">{formatPrice(stock.recommendation.stopLoss)}</p>
          </div>
        </div>
      </div>

      {/* Reasoning */}
      <div className="mb-4">
        <p className="text-sm text-gray-700 leading-relaxed">
          {stock.recommendation.reasoning}
        </p>
      </div>

      {/* Technical Indicators Preview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="text-center">
          <p className="text-xs text-gray-600">RSI</p>
          <p className={`font-medium ${
            stock.technicalIndicators.rsi < 30 ? 'text-success-600' :
            stock.technicalIndicators.rsi > 70 ? 'text-danger-600' : 'text-gray-900'
          }`}>
            {stock.technicalIndicators.rsi.toFixed(1)}
          </p>
        </div>
        
        <div className="text-center">
          <p className="text-xs text-gray-600">Volume Ratio</p>
          <p className={`font-medium ${
            stock.technicalIndicators.volume.ratio > 1.5 ? 'text-success-600' : 'text-gray-900'
          }`}>
            {stock.technicalIndicators.volume.ratio.toFixed(1)}x
          </p>
        </div>
        
        <div className="text-center">
          <p className="text-xs text-gray-600">Volatility</p>
          <p className="font-medium text-gray-900">
            {(stock.riskAssessment.volatility * 100).toFixed(1)}%
          </p>
        </div>
        
        <div className="text-center">
          <p className="text-xs text-gray-600">Liquidity</p>
          <p className={`font-medium capitalize ${
            stock.riskAssessment.liquidity === 'high' ? 'text-success-600' :
            stock.riskAssessment.liquidity === 'medium' ? 'text-warning-600' : 'text-danger-600'
          }`}>
            {stock.riskAssessment.liquidity}
          </p>
        </div>
      </div>

      {/* Expandable Details */}
      <div className="border-t border-gray-200 pt-4">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center justify-between w-full text-sm text-gray-600 hover:text-gray-900 transition-colors"
        >
          <span>View Details</span>
          {isExpanded ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </button>

        {isExpanded && (
          <div className="mt-4 space-y-4">
            {/* Risk Factors */}
            {stock.riskAssessment.factors.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                  <AlertTriangle className="h-4 w-4 mr-1 text-warning-600" />
                  Risk Factors
                </h4>
                <div className="space-y-2">
                  {stock.riskAssessment.factors.slice(0, 3).map((factor, index) => (
                    <div key={index} className="flex items-start space-x-2">
                      <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                        factor.severity <= 3 ? 'bg-success-500' :
                        factor.severity <= 6 ? 'bg-warning-500' : 'bg-danger-500'
                      }`} />
                      <p className="text-sm text-gray-700">{factor.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Trading Windows */}
            {stock.tradingWindows && stock.tradingWindows.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                  <Clock className="h-4 w-4 mr-1 text-primary-600" />
                  Optimal Trading Windows
                </h4>
                <div className="space-y-1">
                  {stock.tradingWindows.map((window, index) => (
                    <div key={index} className="flex justify-between text-sm">
                      <span className="text-gray-700">
                        {window.startTime} - {window.endTime}
                      </span>
                      <span className="text-gray-600">
                        {(window.probability * 100).toFixed(0)}% probability
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent News */}
            {stock.news && stock.news.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                  <Activity className="h-4 w-4 mr-1 text-primary-600" />
                  Recent News
                </h4>
                <div className="space-y-2">
                  {stock.news.slice(0, 2).map((newsItem, index) => (
                    <div key={index} className="text-sm">
                      <p className="text-gray-700 line-clamp-2">{newsItem.headline}</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs text-gray-500">{newsItem.source}</span>
                        <span className={`text-xs px-2 py-1 rounded ${
                          newsItem.sentiment === 'positive' ? 'bg-success-100 text-success-700' :
                          newsItem.sentiment === 'negative' ? 'bg-danger-100 text-danger-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {newsItem.sentiment}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="pt-2">
              <Link
                to={`/stock/${stock.ticker}`}
                className="btn-primary w-full text-center"
              >
                View Full Analysis
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StockCard;